/**
 * Testing Timeline Tab (Tab 1)
 * 
 * Timeline-based audit planning interface
 * Features:
 * - Gantt chart style timeline
 * - Configurable view (Week/Month/Quarter)
 * - Collapsible cycle sections
 * - Timeline bars with user avatars
 * - API integration for data and auditor assignments
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Tooltip, Select, Spin, message, Popover, Button, Dropdown } from 'antd';
import { DownOutlined, RightOutlined, LoadingOutlined, DeleteOutlined, UserOutlined, CloseOutlined, MoreOutlined, EditOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../../store';
import { 
  setTimelineControls, 
  setCustomPlanningTasks,
} from '../../../../store/slices/auditPlanSlice';
import { setAuditors } from '../../../../store/slices/controlActivitiesSlice';
import {
  getTimelineControls,
  TimelineCycleGroup,
  createTimeslot,
  updateTimeslot,
  deleteTimeslot,
  CreateTimeslotRequest,
  UpdateTimeslotRequest,
  getCustomPlanningTasks,
  CustomPlanTask,
  createCustomPlan,
  updateCustomPlan,
  deleteCustomPlan,
  createCustomTimeslot,
  updateCustomTimeslot,
  deleteCustomTimeslot,
  CreateCustomPlanRequest,
  UpdateCustomPlanRequest,
  CreateCustomTimeslotRequest,
  UpdateCustomTimeslotRequest,
} from '../../../../services/auditPlanService';
import { getAuditors } from '../../../../services/controlActivitiesService';
import AddItemButton from '../../../../components/common/AddItemButton';
import RightContentDrawer from '../../../../components/common/RightContentDrawer';
import ButtonGroup from '../../../../components/common/ButtonGroup';
import ConfirmModal from '../../../../components/common/ConfirmModal';
import trashIcon from '../../../../assets/icons/ic-trash.svg';

// ========================================
// Types
// ========================================

type TimelineView = 'week' | 'month' | 'quarter';

interface TimelineAssignment {
  id: string;
  userId: string;
  userName: string;
  startDate: string; // ISO date string
  endDate: string;   // ISO date string
}

// Drag selection state
interface DragSelection {
  controlId: string;
  startColumnIndex: number;
  endColumnIndex: number;
}

// Popover mode
type PopoverMode = 'create' | 'edit';

interface ControlItem {
  id: string;
  controlCode: string;
  riskRanking: 'High' | 'Med' | 'Low' | 'Unranked' | 'null';
  testingStatus: number; // percentage
  controlDescription: {
    what: string;
    when?: string;
    who?: string;
    why?: string;
    how?: string;
    evidence?: string;
  };
  assignments: TimelineAssignment[];
}

interface CycleData {
  id: string;
  cycleCode: string;
  cycleName: string;
  controls: ControlItem[];
}

// ========================================
// Helper: Map API data to component format
// ========================================

const mapTimelineDataToCycles = (cycleGroups: TimelineCycleGroup[]): CycleData[] => {
  return cycleGroups.map(group => ({
    id: group.cycleId,
    cycleCode: group.cycleCode,
    cycleName: group.cycleName,
    controls: group.controls.map(control => ({
      id: control.id,
      controlCode: control.controlCode,
      riskRanking: control.riskRanking,
      testingStatus: control.testingStatus,
        controlDescription: {
        what: control.controlDescription.what,
        when: control.controlDescription.when,
        who: control.controlDescription.who,
        why: control.controlDescription.why,
        how: control.controlDescription.how,
        evidence: control.controlDescription.evidence,
      },
      assignments: control.timeslots.map(ts => ({
        id: ts.id,
        userId: ts.auditorId,
        userName: ts.auditorName,
        startDate: ts.startDate,
        endDate: ts.endDate,
      })),
    })),
  }));
};

// ========================================
// Styled Components
// ========================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const TimelineSelect = styled(Select)`
  min-width: 120px;
  height: 36px;
  
  .ant-select-selector {
    border-radius: 8px !important;
    border-color: #E5E7EB !important;
    font-family: Inter;
    font-size: 14px;
    font-weight: 500;
  }

  &:hover .ant-select-selector {
    border-color: #2563EB !important;
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: #2563EB !important;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1) !important;
  }
`;

const CycleSection = styled.div<{ $bgColor: string }>`
  background: ${props => props.$bgColor};
  padding: 16px 2px 2px 2px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const CycleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  margin-left: 16px;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const CollapseIcon = styled.div`
  display: flex;
  align-items: center;
  color: #6B7280;
  transition: transform 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CycleTitle = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: #1F2937;
`;

// ========================================
// Two-Table Layout: Sticky Left + Scrollable Right
// ========================================

const TableContainer = styled.div`
  display: flex;
  background: white;
  border-radius: 6px;
  overflow: hidden;
`;

// Left table wrapper - sticky
const StickyTableWrapper = styled.div`
  flex-shrink: 0;
  background: white;
  z-index: 10;
  border-right: 2px solid #D1D5DB;
`;

// Right table wrapper - scrollable
const ScrollableTableWrapper = styled.div`
  flex: 1;
  overflow-x: auto;
`;

const StickyTable = styled.table`
  border-collapse: collapse;
  table-layout: fixed;
`;

const ScrollableTable = styled.table`
  border-collapse: collapse;
  min-width: max-content;
  overflow: visible;
`;

// Base row height and dynamic calculation - MUST be before TableBody
const BASE_ROW_HEIGHT = 130; // Height for up to 4 bars
const MIN_BARS_FIT = 4; // Minimum number of bars that fit in base height
const HEADER_HEIGHT = 40;

const TableHead = styled.thead`
  background: #F8FAFC;
  
  th {
    padding: 8px;
    font-family: Inter;
    font-weight: 500;
    font-size: 12px;
    color: #6B7280;
    text-align: left;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    white-space: nowrap;
    
    &:last-child {
      border-right: none;
    }
  }
`;

const TableBody = styled.tbody`
  overflow: visible;
  
  tr {
    transition: background 0.2s;
    /* Height is controlled via inline styles for dynamic row heights */
    min-height: ${BASE_ROW_HEIGHT}px;
    overflow: visible;
  }
  
  td {
    padding: 6px 8px;
    font-family: Inter;
    font-size: 14px;
    color: #1F2937;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    vertical-align: top;
    min-height: ${BASE_ROW_HEIGHT}px;
    box-sizing: border-box;
    /* Note: overflow hidden for sticky cells, visible for timeline cells */
    
    &:last-child {
      border-right: none;
    }
  }
  
  tr:last-child td {
    border-bottom: none;
  }
`;

// Sticky column widths
const STICKY_COLUMN_WIDTHS = {
  controls: 120,
  risk: 120,
  testingStatus: 100,
  description: 200,
};

// Styled cells for sticky table
const StickyHeaderCell = styled.th<{ $width: number }>`
  width: ${props => props.$width}px;
  min-width: ${props => props.$width}px;
  max-width: ${props => props.$width}px;
  height: ${HEADER_HEIGHT}px;
  background: #F8FAFC;
`;

const StickyBodyCell = styled.td<{ $width: number; $rowHeight?: number }>`
  width: ${props => props.$width}px;
  min-width: ${props => props.$width}px;
  max-width: ${props => props.$width}px;
  height: ${props => props.$rowHeight || BASE_ROW_HEIGHT}px !important;
  max-height: ${props => props.$rowHeight || BASE_ROW_HEIGHT}px !important;
  box-sizing: border-box;
  overflow: hidden;
  background: white;
  vertical-align: top;
  padding: 8px !important;
  position: relative;
  
  & > * {
    max-height: ${props => (props.$rowHeight || BASE_ROW_HEIGHT) - 16}px;
    overflow: hidden;
  }
`;

// Hover sync - handled via data attribute
const StickyRow = styled.tr<{ $isHovered?: boolean; $rowHeight?: number }>`
  height: ${props => props.$rowHeight || BASE_ROW_HEIGHT}px;
  max-height: ${props => props.$rowHeight || BASE_ROW_HEIGHT}px;
  
  ${props => props.$isHovered && `
    background: #F9FAFB !important;
    
    td {
      background: #F9FAFB !important;
    }
  `}
`;

const ScrollableRow = styled.tr<{ $isHovered?: boolean; $rowHeight?: number }>`
  height: ${props => props.$rowHeight || BASE_ROW_HEIGHT}px;
  max-height: ${props => props.$rowHeight || BASE_ROW_HEIGHT}px;
  overflow: visible;
  
  ${props => props.$isHovered && `
    background: #F9FAFB !important;
  `}
`;

// Column widths based on view mode
const COLUMN_WIDTHS = {
  week: 120,   // Increased to fit text like "27 May-2 Jun '25"
  month: 160,
  quarter: 240,
};

const TimelineColumnHeader = styled.th<{ $viewMode?: 'week' | 'month' | 'quarter' }>`
  min-width: ${props => COLUMN_WIDTHS[props.$viewMode || 'week']}px;
  width: ${props => COLUMN_WIDTHS[props.$viewMode || 'week']}px;
  height: ${HEADER_HEIGHT}px;
  text-align: center !important;
  position: relative;
  cursor: default;
`;

// Control ID Chip (from ControlActivitiesPage.tsx)
const ControlIdChip = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    opacity: 0.8;
  }
`;

// Risk Ranking Chip (from ControlActivitiesPage.tsx)
const RiskRankingChip = styled.span<{ $level: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: default;
  background: white;

  ${props => {
    switch (props.$level) {
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

// Control Description (from ControlActivitiesPage.tsx)
const DescriptionText = styled.div<{ $maxHeight?: number }>`
  font-size: 14px;
  color: #1F2937;
  height: ${props => (props.$maxHeight || BASE_ROW_HEIGHT) - 16}px;
  max-height: ${props => (props.$maxHeight || BASE_ROW_HEIGHT) - 16}px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  
  .what-label {
    font-weight: 700;
    color: #1F2937;
  }
  
  .what-text {
    font-weight: 400;
    color: #6B7280;
  }
  
  .when-text {
    font-size: 13px;
    color: #9CA3AF;
    margin-top: 4px;
  }
`;

// Bar height and spacing for stacking (must be defined before TimelineCell)
const BAR_HEIGHT = 24;
const BAR_GAP = 4;
const BAR_PADDING_TOP = 4;

// Timeline Cell - needs to allow overflow for cross-cell bars
// Dynamic row height based on overlapping assignments
const TimelineCell = styled.td<{ $viewMode?: 'week' | 'month' | 'quarter'; $rowHeight?: number }>`
  position: relative;
  min-width: ${props => COLUMN_WIDTHS[props.$viewMode || 'week']}px;
  width: ${props => COLUMN_WIDTHS[props.$viewMode || 'week']}px;
  height: ${props => props.$rowHeight || BASE_ROW_HEIGHT}px;
  max-height: ${props => props.$rowHeight || BASE_ROW_HEIGHT}px;
  box-sizing: border-box;
  padding: 0 !important;
  overflow: visible !important;
`;

// Timeline bar that can span across multiple cells

const TimelineBar = styled.div<{ 
  $leftPx: number; 
  $widthPx: number; 
  $color: string;
  $isStart?: boolean;
  $isEnd?: boolean;
  $rowIndex?: number;
}>`
  position: absolute;
  top: ${props => BAR_PADDING_TOP + (props.$rowIndex || 0) * (BAR_HEIGHT + BAR_GAP)}px;
  left: ${props => props.$leftPx}px;
  width: ${props => props.$widthPx}px;
  height: ${BAR_HEIGHT}px;
  background: ${props => props.$color};
  border-radius: ${props => {
    const left = props.$isStart ? '12px' : '0';
    const right = props.$isEnd ? '12px' : '0';
    return `${left} ${right} ${right} ${left}`;
  }};
  display: flex;
  align-items: center;
  gap: 6px;
  padding-left: ${props => props.$isStart ? '2px' : '0'};
  padding-right: ${props => props.$isEnd ? '2px' : '0'};
  z-index: ${props => 10 + (props.$rowIndex || 0)};
  pointer-events: auto;
  overflow: hidden;
`;

const UserAvatar = styled.div<{ $bgColor: string }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${props => props.$bgColor};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  flex-shrink: 0;
  cursor: pointer;
`;

const AuditorName = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #1F2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Styled components for Select auditor display
const SelectAuditorCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SelectAvatar = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #EFF6FF;
  border-radius: 50%;
  font-family: Inter;
  font-weight: 600;
  font-size: 11px;
  color: #1E40AF;
  flex-shrink: 0;
`;

const SelectName = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
`;

const UserTooltipContent = styled.div`
  padding: 8px;
  
  .user-name {
    font-weight: 600;
    font-size: 14px;
    color: #1F2937;
    margin-bottom: 4px;
  }
  
  .user-role {
    font-size: 12px;
    color: #6B7280;
    margin-bottom: 8px;
  }
  
  .user-dates {
    font-size: 12px;
    color: #6B7280;
  }
`;

// ========================================
// Custom Planning Tasks Styled Components
// ========================================

const TaskNameCell = styled(StickyBodyCell)`
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  max-height: calc(1.4em * 5);
  word-break: break-word;
`;

const TaskActionsCell = styled(StickyBodyCell)`
  text-align: center;
`;

const ActionButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 18px;
  }
`;

const TaskEmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: white;
  border-radius: 6px;
`;

const TaskEmptyText = styled.div`
  color: #6B7280;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 16px;
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MenuItemText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
`;

const MenuItemTextDanger = styled(MenuItemText)`
  color: #DC2626;
`;


const FormLabel = styled.label`
  display: block;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const FormInput = styled.textarea`
  width: 100%;
  min-height: 400px;
  padding: 12px;
  font-family: Inter;
  font-size: 14px;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #2563EB;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

// const EmptyStateWrapper = styled.div`
//   text-align: center;
//   padding: 60px 20px;
//   background: white;
//   border-radius: 8px;
// `;

// const EmptyStateText = styled.div`
//   color: #6B7280;
//   font-family: Inter;
//   font-size: 15px;
//   font-weight: 500;
// `;

// Selection highlight overlay
const SelectionHighlight = styled.div<{ $isSelected: boolean; $isInRange: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.$isSelected || props.$isInRange ? 'rgba(37, 99, 235, 0.15)' : 'transparent'};
  border: ${props => props.$isSelected || props.$isInRange ? '2px solid rgba(37, 99, 235, 0.4)' : 'none'};
  pointer-events: none;
  z-index: 2;
`;

// Clickable timeline cell area
const TimelineCellContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: crosshair;
  z-index: 1;
  background: transparent;
`;

// Popover container styles
const PopoverContent = styled.div`
  min-width: 280px;
  padding: 8px 0;
`;

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
  margin-bottom: 16px;
  padding: 0 8px;
`;

const PopoverCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
  border-radius: 4px;
  margin-right: -8px;
  margin-top: -8px;
  transition: all 0.2s;
  
  &:hover {
    color: #6B7280;
    background: #F3F4F6;
  }
`;

const PopoverSection = styled.div`
  margin-bottom: 16px;
`;

const PopoverLabel = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #6B7280;
  margin-bottom: 8px;
`;

const DateRangeDisplay = styled.div`
  font-family: Inter;
  font-size: 13px;
  color: #1F2937;
  padding: 8px 12px;
  background: #F3F4F6;
  border-radius: 6px;
`;

const PopoverActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const DeleteButton = styled(Button)`
  color: #EF4444;
  border-color: #EF4444;
  height: 36px;
  
  &:hover {
    color: #DC2626 !important;
    border-color: #DC2626 !important;
    background: #FEF2F2 !important;
  }
`;

// Clickable timeline bar wrapper
const ClickableTimelineBar = styled(TimelineBar)`
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;


// ========================================
// Helper Functions
// ========================================

const CYCLE_COLORS = ['#E3F2FD', '#F3E5F5', '#E8F5E9', '#FFF3E0', '#FFEBEE', '#E0F7FA'];

const AVATAR_COLORS = ['#F97316', '#8B5CF6', '#10B981', '#3B82F6', '#EC4899', '#6366F1'];

const getAvatarColor = (userId: string): string => {
  const index = userId.charCodeAt(userId.length - 1) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

const getBarColor = (index: number): string => {
  const colors = ['#DBEAFE', '#E9D5FF', '#D1FAE5', '#FED7AA', '#FECACA', '#A5F3FC'];
  return colors[index % colors.length];
};

// Generate timeline columns based on view type
// fiscalYearEndDate: ISO date string (e.g., "2026-04-01T06:00:00.000Z")
// Date range: 1 year before fiscal year end date to fiscal year end date
const generateTimelineColumns = (view: TimelineView, fiscalYear: number, fiscalYearEndDate?: string): { key: string; label: string; weekNum?: number; date?: Date }[] => {
  const columns: { key: string; label: string; weekNum?: number; date?: Date }[] = [];
  
  // Calculate start date (1 year before fiscal year end date)
  const endDate = fiscalYearEndDate ? new Date(fiscalYearEndDate) : new Date(fiscalYear, 11, 31);
  const startDate = new Date(endDate);
  startDate.setFullYear(startDate.getFullYear() - 1);
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  switch (view) {
    case 'week':
      // Generate 52 weeks from start date
      for (let weekNum = 0; weekNum < 52; weekNum++) {
        const weekStart = new Date(startDate);
        weekStart.setDate(weekStart.getDate() + (weekNum * 7));
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        
        const startDay = weekStart.getDate();
        const endDay = weekEnd.getDate();
        const startMonth = monthNames[weekStart.getMonth()];
        const endMonth = monthNames[weekEnd.getMonth()];
        const startYear = weekStart.getFullYear().toString().slice(-2);
        const endYear = weekEnd.getFullYear().toString().slice(-2);
        
        // Format: "1-7 Jan '25" or "29 Jan '25-4 Feb '25"
        let label: string;
        if (startMonth === endMonth && weekStart.getFullYear() === weekEnd.getFullYear()) {
          label = `${startDay}-${endDay} ${startMonth} '${startYear}`;
        } else if (weekStart.getFullYear() === weekEnd.getFullYear()) {
          label = `${startDay} ${startMonth}-${endDay} ${endMonth} '${startYear}`;
        } else {
          label = `${startDay} ${startMonth} '${startYear}-${endDay} ${endMonth} '${endYear}`;
        }
        
        columns.push({
          key: `week-${weekNum + 1}`,
          label,
          weekNum: weekNum + 1,
          date: weekStart,
        });
      }
      break;
    case 'month':
      // Generate 12 months from start date
      for (let i = 0; i < 12; i++) {
        const monthDate = new Date(startDate);
        monthDate.setMonth(monthDate.getMonth() + i);
        const monthIndex = monthDate.getMonth();
        const year = monthDate.getFullYear();
        columns.push({
          key: `month-${i}`,
          label: `${monthNames[monthIndex]} ${year}`,
          date: monthDate,
        });
      }
      break;
    case 'quarter':
      // Generate 4 quarters from start date with date range labels
      for (let i = 0; i < 4; i++) {
        const quarterStart = new Date(startDate);
        quarterStart.setMonth(quarterStart.getMonth() + (i * 3));
        
        const quarterEnd = new Date(quarterStart);
        quarterEnd.setMonth(quarterEnd.getMonth() + 3);
        quarterEnd.setDate(quarterEnd.getDate() - 1); // Last day of quarter
        
        const startDay = quarterStart.getDate();
        const endDay = quarterEnd.getDate();
        const startMonth = monthNames[quarterStart.getMonth()];
        const endMonth = monthNames[quarterEnd.getMonth()];
        const startYear = quarterStart.getFullYear().toString().slice(-2);
        const endYear = quarterEnd.getFullYear().toString().slice(-2);
        
        // Format: "1 Apr '25 - 30 Jun '25" or "1 Apr - 30 Jun '25" if same year
        let label: string;
        if (quarterStart.getFullYear() === quarterEnd.getFullYear()) {
          label = `${startDay} ${startMonth} - ${endDay} ${endMonth} '${startYear}`;
        } else {
          label = `${startDay} ${startMonth} '${startYear} - ${endDay} ${endMonth} '${endYear}`;
        }
        
        columns.push({ 
          key: `q${i + 1}`, 
          label,
          date: quarterStart,
        });
      }
      break;
  }
  
  return columns;
};

// Get cell width based on view mode (must match CSS COLUMN_WIDTHS)
const getCellWidth = (view: TimelineView): number => {
  switch (view) {
    case 'week': return 120;
    case 'month': return 160;
    case 'quarter': return 240;
    default: return 120;
  }
};

// Minimum bar width to show avatar + name
const MIN_BAR_WIDTH = 24;

// Calculate bar position across all columns
// Uses fiscalYearEndDate to determine the 1-year date range
const calculateTimelineBarPosition = (
  assignment: TimelineAssignment,
  view: TimelineView,
  totalColumns: number,
  fiscalYear: number,
  fiscalYearEndDate?: string
): { startColumnIndex: number; leftOffset: number; totalWidth: number; isStart: boolean; isEnd: boolean } | null => {
  const assignmentStartDate = new Date(assignment.startDate);
  const assignmentEndDate = new Date(assignment.endDate);
  const cellWidth = getCellWidth(view);
  
  // Calculate timeline date range (1 year before fiscal year end date)
  const timelineEndDate = fiscalYearEndDate ? new Date(fiscalYearEndDate) : new Date(fiscalYear, 11, 31);
  const timelineStartDate = new Date(timelineEndDate);
  timelineStartDate.setFullYear(timelineStartDate.getFullYear() - 1);
  
  // Helper to get days difference
  const getDaysDiff = (date1: Date, date2: Date) => {
    return Math.floor((date1.getTime() - date2.getTime()) / (24 * 60 * 60 * 1000));
  };
  
  if (view === 'week') {
    // Calculate which week column the assignment starts and ends in
    const daysFromStart = getDaysDiff(assignmentStartDate, timelineStartDate);
    const daysFromStartToEnd = getDaysDiff(assignmentEndDate, timelineStartDate);
    
    const startColumnIndex = Math.floor(daysFromStart / 7);
    const endColumnIndex = Math.floor(daysFromStartToEnd / 7);
    
    // Skip if completely outside visible range
    if (startColumnIndex >= totalColumns || endColumnIndex < 0) return null;
    
    // Calculate offset within start column (days into the week)
    const daysIntoStartWeek = daysFromStart % 7;
    const startOffset = Math.max(0, daysIntoStartWeek) / 7;
    
    // Calculate offset within end column
    const daysIntoEndWeek = daysFromStartToEnd % 7;
    const endOffset = (daysIntoEndWeek + 1) / 7;
    
    const leftOffset = startOffset * cellWidth;
    
    // Calculate total width based on whether start and end are in the same column
    let totalWidth: number;
    if (endColumnIndex === startColumnIndex) {
      // Same column: width is from startOffset to endOffset within that column
      totalWidth = (endOffset - startOffset) * cellWidth;
    } else {
      // Different columns: start column remainder + full columns + end column portion
    const numFullColumns = Math.max(0, endColumnIndex - startColumnIndex - 1);
      const startColumnWidth = (1 - startOffset) * cellWidth;
      const endColumnWidth = endOffset * cellWidth;
      totalWidth = startColumnWidth + (numFullColumns * cellWidth) + endColumnWidth;
    }
    
    return {
      startColumnIndex: Math.max(0, startColumnIndex),
      leftOffset,
      totalWidth: Math.max(totalWidth, MIN_BAR_WIDTH),
      isStart: true,
      isEnd: true,
    };
  } else if (view === 'month') {
    // Calculate month index relative to timeline start date
    const getMonthsFromTimelineStart = (date: Date) => {
      return (date.getFullYear() - timelineStartDate.getFullYear()) * 12 + 
             (date.getMonth() - timelineStartDate.getMonth());
    };
    
    const rawStartMonthIndex = getMonthsFromTimelineStart(assignmentStartDate);
    const rawEndMonthIndex = getMonthsFromTimelineStart(assignmentEndDate);
    
    // Clamp to visible range
    const startMonthIndex = Math.max(0, Math.min(11, rawStartMonthIndex));
    const endMonthIndex = Math.max(0, Math.min(11, rawEndMonthIndex));
    
    // Calculate offset within start month
    let startOffset: number;
    if (rawStartMonthIndex < 0) {
      // Date is before timeline start - position at start of first column
      startOffset = 0;
    } else {
      const startDayOfMonth = assignmentStartDate.getDate();
      const daysInStartMonth = new Date(assignmentStartDate.getFullYear(), assignmentStartDate.getMonth() + 1, 0).getDate();
      startOffset = (startDayOfMonth - 1) / daysInStartMonth;
    }
    
    // Calculate offset within end month
    let endOffset: number;
    if (rawEndMonthIndex > 11) {
      // Date is after timeline end - position at end of last column
      endOffset = 1;
    } else {
      const endDayOfMonth = assignmentEndDate.getDate();
      const daysInEndMonth = new Date(assignmentEndDate.getFullYear(), assignmentEndDate.getMonth() + 1, 0).getDate();
      endOffset = endDayOfMonth / daysInEndMonth;
    }
    
    const startColumnIdx = startMonthIndex;
    const endColumnIdx = endMonthIndex;
    
    // Skip if completely outside visible range
    if (rawStartMonthIndex >= 12 || rawEndMonthIndex < 0) return null;
    
    const leftOffset = startOffset * cellWidth;
    
    // Calculate total width based on whether start and end are in the same column
    let totalWidth: number;
    if (endColumnIdx === startColumnIdx) {
      // Same column: width is from startOffset to endOffset within that column
      totalWidth = (endOffset - startOffset) * cellWidth;
    } else {
      // Different columns: start column remainder + full columns + end column portion
      const numFullColumns = Math.max(0, endColumnIdx - startColumnIdx - 1);
      const startColumnWidth = (1 - startOffset) * cellWidth;
      const endColumnWidth = endOffset * cellWidth;
      totalWidth = startColumnWidth + (numFullColumns * cellWidth) + endColumnWidth;
    }
    
    return {
      startColumnIndex: Math.max(0, startColumnIdx),
      leftOffset,
      totalWidth: Math.max(totalWidth, MIN_BAR_WIDTH),
      isStart: true,
      isEnd: true,
    };
  } else {
    // Quarter view - calculate relative to timeline start date
    const getQuartersFromTimelineStart = (date: Date) => {
      const monthsFromStart = (date.getFullYear() - timelineStartDate.getFullYear()) * 12 + 
                              (date.getMonth() - timelineStartDate.getMonth());
      return Math.floor(monthsFromStart / 3);
    };
    
    const startQuarterIndex = getQuartersFromTimelineStart(assignmentStartDate);
    const endQuarterIndex = getQuartersFromTimelineStart(assignmentEndDate);
    
    // Calculate day position within the quarter (more precise than month-based)
    const quarterStartMonth = Math.floor(assignmentStartDate.getMonth() / 3) * 3;
    const quarterStart = new Date(assignmentStartDate.getFullYear(), quarterStartMonth, 1);
    const quarterEnd = new Date(assignmentStartDate.getFullYear(), quarterStartMonth + 3, 0);
    const daysInQuarter = Math.ceil((quarterEnd.getTime() - quarterStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const startDayInQuarter = Math.ceil((assignmentStartDate.getTime() - quarterStart.getTime()) / (1000 * 60 * 60 * 24));
    const startOffset = startDayInQuarter / daysInQuarter;
    
    const endQuarterStartMonth = Math.floor(assignmentEndDate.getMonth() / 3) * 3;
    const endQuarterStart = new Date(assignmentEndDate.getFullYear(), endQuarterStartMonth, 1);
    const endQuarterEnd = new Date(assignmentEndDate.getFullYear(), endQuarterStartMonth + 3, 0);
    const daysInEndQuarter = Math.ceil((endQuarterEnd.getTime() - endQuarterStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const endDayInQuarter = Math.ceil((assignmentEndDate.getTime() - endQuarterStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const endOffset = endDayInQuarter / daysInEndQuarter;
    
    const startColumnIndex = startQuarterIndex;
    const endColumnIndex = endQuarterIndex;
    
    // Skip if completely outside visible range
    if (startColumnIndex >= totalColumns || endColumnIndex < 0) return null;
    
    const leftOffset = startOffset * cellWidth;
    
    // Calculate total width based on whether start and end are in the same column
    let totalWidth: number;
    if (endColumnIndex === startColumnIndex) {
      // Same column: width is from startOffset to endOffset within that column
      totalWidth = (endOffset - startOffset) * cellWidth;
    } else {
      // Different columns: start column remainder + full columns + end column portion
      const numFullColumns = Math.max(0, endColumnIndex - startColumnIndex - 1);
      const startColumnWidth = (1 - startOffset) * cellWidth;
      const endColumnWidth = endOffset * cellWidth;
      totalWidth = startColumnWidth + (numFullColumns * cellWidth) + endColumnWidth;
    }
    
    return {
      startColumnIndex: Math.max(0, startColumnIndex),
      leftOffset,
      totalWidth: Math.max(totalWidth, MIN_BAR_WIDTH),
      isStart: true,
      isEnd: true,
    };
  }
};

// ========================================
// Component
// ========================================

const TestingTimelineTab: React.FC = () => {
  // Navigation
  const navigate = useNavigate();
  const { isAdmin } = useSelector((state: RootState) => state.auth);

  // Redux state
  const dispatch = useDispatch<AppDispatch>();
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const { user, isExternalUser } = useSelector((state: RootState) => state.auth);
  const auditPlanState = useSelector((state: RootState) => state.auditPlan);
  
  // Get cycles from Redux (memoized to avoid re-mapping on every render)
  const cycles = useMemo(() => {
    if (auditPlanState?.timelineCycleGroups?.length > 0) {
      return mapTimelineDataToCycles(auditPlanState.timelineCycleGroups);
    }
    return [];
  }, [auditPlanState?.timelineCycleGroups]);
  
  // Get custom planning tasks from Redux
  const customPlanningTasks = auditPlanState?.customPlanningTasks || [];
  
  const [loading, setLoading] = useState(false);
  const [loadingCustomTasks, setLoadingCustomTasks] = useState(false);
  const [submitCustomTask, setSubmitCustomTask] = useState(false);

  const [collapsedCycles, setCollapsedCycles] = useState<Set<string>>(new Set());
  
  // Custom planning tasks state
  const [planningTasksCollapsed, setPlanningTasksCollapsed] = useState(false);
  const [customTaskDrawerOpen, setCustomTaskDrawerOpen] = useState(false);
  const [editingCustomTask, setEditingCustomTask] = useState<CustomPlanTask | null>(null);
  const [customTaskName, setCustomTaskName] = useState('');
  const [deleteTaskModalOpen, setDeleteTaskModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [deletingTask, setDeletingTask] = useState(false);
  
  // Custom task drag selection state
  const [isCustomTaskDragging, setIsCustomTaskDragging] = useState(false);
  const [customTaskDragSelection, setCustomTaskDragSelection] = useState<DragSelection | null>(null);
  const customTaskDragStartRef = useRef<{ taskId: string; columnIndex: number } | null>(null);
  
  // Custom task popover state
  const [customTaskPopoverOpen, setCustomTaskPopoverOpen] = useState(false);
  const [customTaskPopoverMode, setCustomTaskPopoverMode] = useState<PopoverMode>('create');
  const [customTaskPopoverPosition, setCustomTaskPopoverPosition] = useState<{ taskId: string; columnIndex: number } | null>(null);
  const [customTaskSelectedAuditorId, setCustomTaskSelectedAuditorId] = useState<string | null>(null);
  const [editingCustomTimeslot, setEditingCustomTimeslot] = useState<TimelineAssignment | null>(null);
  const [savingCustomTimeslot, setSavingCustomTimeslot] = useState(false);
  const [deletingCustomTimeslot, setDeletingCustomTimeslot] = useState(false);

  const [timelineView, setTimelineView] = useState<TimelineView>('week');
  
  // Drag selection state
  const [isDragging, setIsDragging] = useState(false);
  const [dragSelection, setDragSelection] = useState<DragSelection | null>(null);
  const dragStartRef = useRef<{ controlId: string; columnIndex: number } | null>(null);
  
  // Hover state for syncing rows between two tables
  const [hoveredControlId, setHoveredControlId] = useState<string | null>(null);
  
  // Popover state
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverMode, setPopoverMode] = useState<PopoverMode>('create');
  const [popoverPosition, setPopoverPosition] = useState<{ controlId: string; columnIndex: number } | null>(null);
  const [selectedAuditorId, setSelectedAuditorId] = useState<string | null>(null);
  const [editingAssignment, setEditingAssignment] = useState<TimelineAssignment | null>(null);
  const [savingAssignment, setSavingAssignment] = useState(false);
  
  // Auditors list from Redux (offline-first)
  const auditors = useSelector((state: RootState) => state.controlActivities.auditors);
  const auditorsLastFetchedCompanyId = useSelector((state: RootState) => state.controlActivities.auditorsLastFetchedCompanyId);

  // Fetch auditors when component mounts (only if not cached for current company)
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");

    if (!companyId) {
      return;
    }
    
    // Already have auditors for this company - skip fetch
    if (auditorsLastFetchedCompanyId === companyId && auditors.length > 0) {
      return;
    }
    
    const fetchAuditorsData = async () => {
      try {
        const response = await getAuditors({
          companyId: companyId,
        });
        dispatch(setAuditors({
          auditors: response,
          companyId: companyId,
        }));
      } catch (error: any) {
        console.error('Failed to fetch auditors:', error);
      }
    };
    
    fetchAuditorsData();
  }, [user, dispatch, auditorsLastFetchedCompanyId, auditors.length]);

  // Fetch timeline data
  // showLoading: if false, do a silent background refetch without showing loading spinner
  const fetchTimelineData = useCallback(async (showLoading = true) => {
    if (!selectedReport?.annualReportId) {
      return;
    }

    if (showLoading) {
    setLoading(true);
    }
    try {
      const response = await getTimelineControls({
        annualReportId: selectedReport.annualReportId,
      });
      
      // Dispatch to Redux - component subscribes to get data
      dispatch(setTimelineControls({
        data: response.data,
        groupedByCycle: response.groupedByCycle,
        totalCount: response.totalCount,
        reportId: selectedReport.annualReportId,
      }));
    } catch (error: any) {
      console.error('Failed to fetch timeline data:', error);
      if (showLoading) {
      message.error(error?.message || 'Failed to load timeline data');
      }
    } finally {
      if (showLoading) {
      setLoading(false);
      }
    }
  }, [selectedReport?.annualReportId, dispatch]);

  // Fetch custom planning tasks
  // showLoading: if false, do a silent background refetch without showing loading spinner
  const fetchCustomPlanningTasks = useCallback(async (showLoading = true) => {
    if (!selectedReport?.annualReportId) {
      return;
    }

    if (showLoading) {
    setLoadingCustomTasks(true);
    }

    try {
      const response = await getCustomPlanningTasks(
        selectedReport.annualReportId,
        {
          skip: 0,
          take: 1000,
          requiresCounts: true,
          sorted: [{ name: 'CreatedAt', direction: 'ascending' as const }],
        }
      );
      
      // Dispatch to Redux
      dispatch(setCustomPlanningTasks({
        data: response.data,
        totalCount: response.totalCount,
        reportId: selectedReport.annualReportId,
      }));
    } catch (error: any) {
      console.error('Failed to fetch custom planning tasks:', error);
      // Don't show error message as this is optional data
    } finally {
      if (showLoading) {
      setLoadingCustomTasks(false);
      }
    }
  }, [selectedReport?.annualReportId, dispatch]);

  // Fetch data when selectedReport changes
  useEffect(() => {
    fetchTimelineData();
    fetchCustomPlanningTasks();
  }, [fetchTimelineData, fetchCustomPlanningTasks]);

  // Handle mouse down on timeline cell (start drag)
  const handleCellMouseDown = useCallback((controlId: string, columnIndex: number, e: React.MouseEvent) => {
    if (!isExternalUser)
    {
      e.preventDefault();
    dragStartRef.current = { controlId, columnIndex };
    setIsDragging(true);
    setDragSelection({
      controlId,
      startColumnIndex: columnIndex,
      endColumnIndex: columnIndex,
    });}
  }, []);

  // Handle mouse enter on timeline cell (during drag)
  const handleCellMouseEnter = useCallback((controlId: string, columnIndex: number) => {
    if (!isDragging || !dragStartRef.current) return;
    
    // Only allow drag within the same control row
    if (dragStartRef.current.controlId !== controlId) return;
    
    setDragSelection(prev => {
      if (!prev) return null;
      return {
        ...prev,
        endColumnIndex: columnIndex,
      };
    });
  }, [isDragging]);

  // Handle mouse up (end drag and show popover)
  const handleCellMouseUp = useCallback((controlId: string, _columnIndex: number) => {
    if (!isDragging || !dragStartRef.current) return;
    
    // Only process if on the same control row
    if (dragStartRef.current.controlId === controlId && dragSelection) {
      const startCol = Math.min(dragSelection.startColumnIndex, dragSelection.endColumnIndex);
      const endCol = Math.max(dragSelection.startColumnIndex, dragSelection.endColumnIndex);
      
      setDragSelection({
        controlId,
        startColumnIndex: startCol,
        endColumnIndex: endCol,
      });
      
      setPopoverPosition({ controlId, columnIndex: startCol });
      setPopoverMode('create');
      setSelectedAuditorId(null);
      setEditingAssignment(null);
      setPopoverOpen(true);
    }
    
    setIsDragging(false);
    dragStartRef.current = null;
  }, [isDragging, dragSelection]);

  // Global mouse up handler to cancel drag if released outside
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        dragStartRef.current = null;
        if (!popoverOpen) {
          setDragSelection(null);
        }
      }
    };
    
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isDragging, popoverOpen]);

  // Global mouse up handler for custom task dragging
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isCustomTaskDragging) {
        setIsCustomTaskDragging(false);
        customTaskDragStartRef.current = null;
        if (!customTaskPopoverOpen) {
          setCustomTaskDragSelection(null);
        }
      }
    };
    
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [isCustomTaskDragging, customTaskPopoverOpen]);

  // Handle click on existing assignment bar
  const handleNavigateToControl = (controlId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/control-activities/${controlId}`);
  };

  const handleAssignmentClick = useCallback((
    e: React.MouseEvent,
    controlId: string,
    assignment: TimelineAssignment
  ) => {
    e.stopPropagation();
    
    setEditingAssignment(assignment);
    setSelectedAuditorId(assignment.userId);
    setPopoverMode('edit');
    setPopoverPosition({ controlId, columnIndex: 0 });
    setDragSelection(null);
    setPopoverOpen(true);
  }, []);

  // Close popover
  const handleClosePopover = useCallback(() => {
    setPopoverOpen(false);
    setDragSelection(null);
    setSelectedAuditorId(null);
    setEditingAssignment(null);
  }, []);

  // Save assignment (create or update) - will be updated after timelineColumns is available
  const handleSaveAssignmentRef = useRef<() => Promise<void>>();
  
  const handleSaveAssignment = useCallback(async () => {
    if (handleSaveAssignmentRef.current) {
      await handleSaveAssignmentRef.current();
    }
  }, []);

  // Delete assignment
  const handleDeleteAssignment = useCallback(async () => {
    if (!editingAssignment) return;

    setSavingAssignment(true);
    try {
      await deleteTimeslot(editingAssignment.id);
      message.success('Assignment removed successfully');
      
      // Silent refetch to update UI, then close popover
      await fetchTimelineData(false);
      handleClosePopover();
    } catch (error: any) {
      console.error('Failed to delete assignment:', error);
      message.error(error?.message || 'Failed to delete assignment');
    } finally {
      setSavingAssignment(false);
    }
  }, [editingAssignment, handleClosePopover, fetchTimelineData]);

  // ========================================
  // Custom Planning Tasks Handlers
  // ========================================

  // Open drawer to create new task
  const handleAddCustomTask = useCallback(() => {
    setEditingCustomTask(null);
    setCustomTaskName('');
    setCustomTaskDrawerOpen(true);
  }, []);

  // Open drawer to edit existing task
  const handleEditCustomTask = useCallback((task: CustomPlanTask) => {
    setEditingCustomTask(task);
    setCustomTaskName(task.name);
    setCustomTaskDrawerOpen(true);
  }, []);

  // Save custom task (create or update)
  const handleSaveCustomTask = useCallback(async () => {
    if (!customTaskName.trim()) {
      message.warning('Please enter a task name');
      return;
    }

    if (!selectedReport?.annualReportId) {
      return;
    }

    setSubmitCustomTask(true);
    try {
      if (editingCustomTask) {
        // Update
        const request: UpdateCustomPlanRequest = {
          customPlanId: editingCustomTask.id,
          name: customTaskName.trim(),
        };
        await updateCustomPlan(request);
        message.success('Task updated successfully');
      } else {
        // Create
        const request: CreateCustomPlanRequest = {
          annualReportId: selectedReport.annualReportId,
          name: customTaskName.trim(),
        };
        await createCustomPlan(request);
        message.success('Task created successfully');
      }
      
      setCustomTaskDrawerOpen(false);
      await fetchCustomPlanningTasks();
    } catch (error: any) {
      console.error('Failed to save custom task:', error);
      message.error(error?.message || 'Failed to save task');
    } finally {
      setSubmitCustomTask(false);
    }
  }, [customTaskName, editingCustomTask, selectedReport?.annualReportId, fetchCustomPlanningTasks]);

  // Open delete task modal
  const openDeleteTaskModal = useCallback((taskId: string) => {
    setTaskToDelete(taskId);
    setDeleteTaskModalOpen(true);
  }, []);

  // Delete custom task
  const handleDeleteCustomTask = useCallback(async () => {
    if (!taskToDelete) return;

    setDeletingTask(true);
    try {
      await deleteCustomPlan(taskToDelete);
      message.success('Task deleted successfully');
      setDeleteTaskModalOpen(false);
      setTaskToDelete(null);
      await fetchCustomPlanningTasks();
    } catch (error: any) {
      console.error('Failed to delete custom task:', error);
      message.error(error?.message || 'Failed to delete task');
    } finally {
      setDeletingTask(false);
    }
  }, [taskToDelete, fetchCustomPlanningTasks]);

  // ========================================
  // Custom Task Timeline Drag Handlers
  // ========================================

  const handleCustomTaskCellMouseDown = useCallback((taskId: string, columnIndex: number, e: React.MouseEvent) => {
    if (e.button !== 0 || isExternalUser) return; // Only left click
    customTaskDragStartRef.current = { taskId, columnIndex };
    setIsCustomTaskDragging(true);
    setCustomTaskDragSelection({
      controlId: taskId, // Reuse controlId field for taskId
      startColumnIndex: columnIndex,
      endColumnIndex: columnIndex,
    });
  }, []);

  const handleCustomTaskCellMouseEnter = useCallback((taskId: string, columnIndex: number) => {
    if (!isCustomTaskDragging || !customTaskDragStartRef.current) return;
    if (customTaskDragStartRef.current.taskId !== taskId) return;

    setCustomTaskDragSelection({
      controlId: taskId,
      startColumnIndex: customTaskDragStartRef.current.columnIndex,
      endColumnIndex: columnIndex,
    });
  }, [isCustomTaskDragging]);

  const handleCustomTaskCellMouseUp = useCallback((taskId: string, _: number) => {
    if (!isCustomTaskDragging || !customTaskDragSelection) return;
    if (customTaskDragSelection.controlId !== taskId) return;

    setIsCustomTaskDragging(false);
    setCustomTaskPopoverMode('create');
    setCustomTaskPopoverPosition({ taskId, columnIndex: customTaskDragSelection.startColumnIndex });
    setCustomTaskSelectedAuditorId(null);
    setEditingCustomTimeslot(null);
    setCustomTaskPopoverOpen(true);
  }, [isCustomTaskDragging, customTaskDragSelection]);

  // Close custom task popover
  const handleCloseCustomTaskPopover = useCallback(() => {
    setCustomTaskPopoverOpen(false);
    setCustomTaskDragSelection(null);
    setCustomTaskSelectedAuditorId(null);
    setEditingCustomTimeslot(null);
  }, []);

  // Save custom timeslot (create or update) - will be updated after timelineColumns is available
  const handleSaveCustomTimeslotRef = useRef<() => Promise<void>>();
  
  const handleSaveCustomTimeslot = useCallback(async () => {
    if (handleSaveCustomTimeslotRef.current) {
      await handleSaveCustomTimeslotRef.current();
    }
  }, []);

  // Delete custom timeslot
  const handleDeleteCustomTimeslot = useCallback(async () => {
    if (!editingCustomTimeslot) return;

    setDeletingCustomTimeslot(true);
    try {
      await deleteCustomTimeslot(editingCustomTimeslot.id);
      message.success('Assignment removed successfully');
      
      // Silent refetch to update UI, then close popover
      await fetchCustomPlanningTasks(false);
      handleCloseCustomTaskPopover();
    } catch (error: any) {
      console.error('Failed to delete custom timeslot:', error);
      message.error(error?.message || 'Failed to delete assignment');
    } finally {
      setDeletingCustomTimeslot(false);
    }
  }, [editingCustomTimeslot, handleCloseCustomTaskPopover, fetchCustomPlanningTasks]);

  // Handle click on existing custom timeslot
  const handleCustomTimeslotClick = useCallback((
    e: React.MouseEvent,
    taskId: string,
    timeslot: TimelineAssignment
  ) => {
    e.stopPropagation();
    
    setEditingCustomTimeslot(timeslot);
    setCustomTaskSelectedAuditorId(timeslot.userId);
    setCustomTaskPopoverMode('edit');
    setCustomTaskPopoverPosition({ taskId, columnIndex: 0 });
    setCustomTaskDragSelection(null);
    setCustomTaskPopoverOpen(true);
  }, []);

  // Get fiscal year from selectedReport or default to current year
  const fiscalYear = selectedReport?.year || new Date().getFullYear();
  const fiscalYearEndDate = selectedReport?.fiscalYearEndDate;

  const timelineColumns = useMemo(() => 
    generateTimelineColumns(timelineView, fiscalYear, fiscalYearEndDate),
    [timelineView, fiscalYear, fiscalYearEndDate]
  );

  // Convert column index to date based on view type
  const getDateFromColumnIndex = useCallback((columnIndex: number, isEndDate: boolean = false): Date => {
    // Use the date from timeline columns (which are based on fiscal year end date)
    const column = timelineColumns[columnIndex];
    if (!column || !column.date) {
      // Fallback to current date if column not found
      return new Date();
    }
    
    const columnDate = new Date(column.date);
    
    if (timelineView === 'week') {
      if (isEndDate) {
        // End of week (6 days after start)
        const endDate = new Date(columnDate);
        endDate.setDate(endDate.getDate() + 6);
        return endDate;
      }
      return columnDate;
    } else if (timelineView === 'month') {
      if (isEndDate) {
        // Last day of the month
        return new Date(columnDate.getFullYear(), columnDate.getMonth() + 1, 0);
      }
      return new Date(columnDate.getFullYear(), columnDate.getMonth(), 1);
    } else {
      // Quarter - columnDate is already the start of the quarter
      if (isEndDate) {
        // Last day of the quarter (3 months after start)
        return new Date(columnDate.getFullYear(), columnDate.getMonth() + 3, 0);
      }
      return columnDate;
    }
  }, [timelineView, timelineColumns]);

  // Format date range for display
  const formatDateRange = useCallback((startCol: number, endCol: number): string => {
    const startDate = getDateFromColumnIndex(Math.min(startCol, endCol), false);
    const endDate = getDateFromColumnIndex(Math.max(startCol, endCol), true);
    
    const formatOptions: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
    return `${startDate.toLocaleDateString('en-US', formatOptions)} - ${endDate.toLocaleDateString('en-US', formatOptions)}`;
  }, [getDateFromColumnIndex]);

  // Update refs with the actual save implementations now that getDateFromColumnIndex is available
  useEffect(() => {
    handleSaveAssignmentRef.current = async () => {
      if (!selectedAuditorId) {
        message.warning('Please select an auditor');
        return;
      }

      setSavingAssignment(true);
      try {
        if (popoverMode === 'create' && dragSelection) {
          const startDate = getDateFromColumnIndex(dragSelection.startColumnIndex, false);
          const endDate = getDateFromColumnIndex(dragSelection.endColumnIndex, true);
          
          const request: CreateTimeslotRequest = {
            auditorId: selectedAuditorId,
            controlId: dragSelection.controlId,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          };
          
          await createTimeslot(request);
          message.success('Auditor assigned successfully');
          
          // Silent refetch to update UI, then close popover
          await fetchTimelineData(false);
          handleClosePopover();
        } else if (popoverMode === 'edit' && editingAssignment && popoverPosition) {
          // For edit mode, we keep the same dates but change the auditor
          const request: UpdateTimeslotRequest = {
            auditTimeslotId: editingAssignment.id,
            auditorId: selectedAuditorId,
            controlId: popoverPosition.controlId,
            startDate: editingAssignment.startDate,
            endDate: editingAssignment.endDate,
          };
          
          await updateTimeslot(request);
          message.success('Assignment updated successfully');
          
          // Silent refetch to update UI, then close popover
          await fetchTimelineData(false);
          handleClosePopover();
        }
      } catch (error: any) {
        console.error('Failed to save assignment:', error);
        message.error(error?.message || 'Failed to save assignment');
      } finally {
        setSavingAssignment(false);
      }
    };
  }, [selectedAuditorId, dragSelection, popoverMode, editingAssignment, popoverPosition, getDateFromColumnIndex, handleClosePopover, fetchTimelineData]);

  useEffect(() => {
    handleSaveCustomTimeslotRef.current = async () => {
      if (!customTaskSelectedAuditorId) {
        message.warning('Please select an auditor');
        return;
      }

      setSavingCustomTimeslot(true);
      try {
        if (customTaskPopoverMode === 'create' && customTaskDragSelection && customTaskPopoverPosition) {
          const startDate = getDateFromColumnIndex(customTaskDragSelection.startColumnIndex, false);
          const endDate = getDateFromColumnIndex(customTaskDragSelection.endColumnIndex, true);
          
          const request: CreateCustomTimeslotRequest = {
            auditorId: customTaskSelectedAuditorId,
            customPlanId: customTaskPopoverPosition.taskId,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          };
          
          await createCustomTimeslot(request);
          message.success('Auditor assigned successfully');
          
          // Silent refetch to update UI, then close popover
          await fetchCustomPlanningTasks(false);
          handleCloseCustomTaskPopover();
        } else if (customTaskPopoverMode === 'edit' && editingCustomTimeslot && customTaskPopoverPosition) {
          // For edit mode, we keep the same dates but change the auditor
          const request: UpdateCustomTimeslotRequest = {
            customTimeslotId: editingCustomTimeslot.id,
            auditorId: customTaskSelectedAuditorId,
            startDate: editingCustomTimeslot.startDate,
            endDate: editingCustomTimeslot.endDate,
          };
          
          await updateCustomTimeslot(request);
          message.success('Assignment updated successfully');
          
          // Silent refetch to update UI, then close popover
          await fetchCustomPlanningTasks(false);
          handleCloseCustomTaskPopover();
        }
      } catch (error: any) {
        console.error('Failed to save custom timeslot:', error);
        message.error(error?.message || 'Failed to save assignment');
      } finally {
        setSavingCustomTimeslot(false);
      }
    };
  }, [customTaskSelectedAuditorId, customTaskDragSelection, customTaskPopoverMode, editingCustomTimeslot, customTaskPopoverPosition, getDateFromColumnIndex, handleCloseCustomTaskPopover, fetchCustomPlanningTasks]);

  const toggleCycleCollapse = (cycleId: string) => {
    setCollapsedCycles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cycleId)) {
        newSet.delete(cycleId);
      } else {
        newSet.add(cycleId);
      }
      return newSet;
    });
  };

  // Timeline view options for Select
  const viewOptions = [
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'quarter', label: 'Quarter' },
  ];

  // Get user initials from name
  const getUserInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  /**
   * Calculate row assignments for timeline bars using overlap detection
   * Instead of simple round-robin, this assigns bars to the first available row
   * that doesn't overlap with their time range
   * @returns Map of assignment id -> row index, and max row count
   */
  const calculateRowAssignments = useCallback((assignments: TimelineAssignment[]): {
    rowMap: Map<string, number>;
    maxRows: number;
  } => {
    if (!assignments || assignments.length === 0) {
      return { rowMap: new Map(), maxRows: 0 };
    }

    // Sort by start date
    const sortedAssignments = [...assignments].sort((a, b) => 
      new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );

    const rowMap = new Map<string, number>();
    // Track occupied time ranges for each row
    // Each row has an array of occupied time ranges
    const rowOccupancy: Array<Array<{ start: number; end: number }>> = [];
    let maxRows = 0;

    for (const assignment of sortedAssignments) {
      const startTime = new Date(assignment.startDate).getTime();
      const endTime = new Date(assignment.endDate).getTime();

      // Find first available row that doesn't overlap
      let assignedRow = -1;
      for (let rowIdx = 0; rowIdx < rowOccupancy.length; rowIdx++) {
        const ranges = rowOccupancy[rowIdx];
        const hasOverlap = ranges.some(range => 
          // Check if ranges overlap (start1 < end2 && start2 < end1)
          startTime < range.end && range.start < endTime
        );
        
        if (!hasOverlap) {
          assignedRow = rowIdx;
          break;
        }
      }

      // If no available row found, create a new one
      if (assignedRow === -1) {
        assignedRow = rowOccupancy.length;
        rowOccupancy.push([]);
      }

      // Add this assignment's time range to the row
      rowOccupancy[assignedRow].push({ start: startTime, end: endTime });
      rowMap.set(assignment.id, assignedRow);
      maxRows = Math.max(maxRows, assignedRow + 1);
    }

    return { rowMap, maxRows };
  }, []);

  /**
   * Calculate the required row height based on max overlapping bars
   */
  const calculateRowHeight = useCallback((maxRows: number): number => {
    // If we have 4 or fewer bars, use base height
    if (maxRows <= MIN_BARS_FIT) {
      return BASE_ROW_HEIGHT;
    }
    // For more bars, increase height dynamically
    // Each additional bar needs BAR_HEIGHT + BAR_GAP more space
    const additionalBars = maxRows - MIN_BARS_FIT;
    return BASE_ROW_HEIGHT + (additionalBars * (BAR_HEIGHT + BAR_GAP));
  }, []);

  /**
   * Get pre-calculated row assignments for a control's assignments
   * Memoized to avoid recalculating on every render
   */
  const controlRowAssignments = useMemo(() => {
    const assignments = new Map<string, { rowMap: Map<string, number>; maxRows: number; rowHeight: number }>();
    
    for (const cycle of cycles) {
      for (const control of cycle.controls) {
        const { rowMap, maxRows } = calculateRowAssignments(control.assignments);
        assignments.set(control.id, {
          rowMap,
          maxRows,
          rowHeight: calculateRowHeight(maxRows),
        });
      }
    }
    
    return assignments;
  }, [cycles, calculateRowAssignments, calculateRowHeight]);

  /**
   * Get pre-calculated row assignments for custom planning tasks
   */
  const customTaskRowAssignments = useMemo(() => {
    const assignments = new Map<string, { rowMap: Map<string, number>; maxRows: number; rowHeight: number }>();
    
    for (const task of customPlanningTasks) {
      // Convert task timeslots to TimelineAssignment format for calculation
      const taskAssignments: TimelineAssignment[] = task.timeslots.map(ts => ({
        id: ts.id,
        userId: ts.userId,
        userName: ts.userName,
        startDate: ts.startDate,
        endDate: ts.endDate,
      }));
      
      const { rowMap, maxRows } = calculateRowAssignments(taskAssignments);
      assignments.set(task.id, {
        rowMap,
        maxRows,
        rowHeight: calculateRowHeight(maxRows),
      });
    }
    
    return assignments;
  }, [customPlanningTasks, calculateRowAssignments, calculateRowHeight]);

  // Render timeline bars for a control - only render in the starting cell
  const renderTimelineBars = (control: ControlItem, columnIndex: number) => {
    // Get pre-calculated row assignments for this control
    const rowAssignment = controlRowAssignments.get(control.id);
    const rowMap = rowAssignment?.rowMap || new Map();
    
    // Sort assignments by start date first
    const sortedAssignments = [...control.assignments].sort((a, b) => 
      new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
    
    // Only render bars that START in this column
    const barsToRender = sortedAssignments.map((assignment, sortedIndex) => {
      const position = calculateTimelineBarPosition(
        assignment,
        timelineView,
        timelineColumns.length,
        fiscalYear,
        fiscalYearEndDate
      );
      
      if (!position || position.startColumnIndex !== columnIndex) {
        return null;
      }
      
      // Get row index from pre-calculated overlap-aware assignment
      const rowIndex = rowMap.get(assignment.id) ?? sortedIndex % MIN_BARS_FIT;
      
      const barColor = getBarColor(sortedIndex);
      const avatarColor = getAvatarColor(assignment.userId);
      
      // Check if this assignment's popover should be open
      const isEditPopoverOpen = popoverOpen && 
        popoverMode === 'edit' && 
        editingAssignment?.id === assignment.id;

      const timelineBarContent = (
          <Tooltip
          key={`${control.id}-${assignment.id}-${sortedIndex}`}
          title={popoverOpen ? null : (
              <UserTooltipContent>
                <div className="user-name">{assignment.userName}</div>
                <div className="user-dates">
                  {new Date(assignment.startDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short' })} - {new Date(assignment.endDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short' })}
                </div>
              <div style={{ marginTop: '8px', fontSize: '11px', color: '#9CA3AF' }}>
                Click to edit or remove
                </div>
              </UserTooltipContent>
          )}
            placement="top"
            overlayInnerStyle={{
              backgroundColor: '#FFFFFF',
              padding: '8px',
              borderRadius: '8px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            }}
            color="#FFFFFF"
        >
          <ClickableTimelineBar
            $leftPx={position.leftOffset}
            $widthPx={position.totalWidth}
            $color={barColor}
            $isStart={position.isStart}
            $isEnd={position.isEnd}
            $rowIndex={rowIndex}
            onClick={(e) => handleAssignmentClick(e, control.id, assignment)}
          >
            <UserAvatar $bgColor={avatarColor}>
              {getUserInitials(assignment.userName)}
            </UserAvatar>
            <AuditorName>{assignment.userName}</AuditorName>
          </ClickableTimelineBar>
          </Tooltip>
      );

      // Wrap with Popover for edit mode
      if (isEditPopoverOpen) {
        return (
          <Popover
            key={`popover-${control.id}-${assignment.id}`}
            content={renderPopoverContent()}
            title={null}
            trigger="click"
            open={true}
            onOpenChange={(open) => !open && handleClosePopover()}
            placement="top"
            overlayInnerStyle={{ 
              boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
              border: 'none',
              minWidth: 300,
            }}
          >
            {timelineBarContent}
          </Popover>
        );
      }

      return timelineBarContent;
    }).filter(Boolean);
    
    return barsToRender.length > 0 ? barsToRender : null;
  };

  // Check if a cell is in the current drag selection range
  const isCellInSelection = (controlId: string, columnIndex: number): boolean => {
    if (!dragSelection || dragSelection.controlId !== controlId) return false;
    const minCol = Math.min(dragSelection.startColumnIndex, dragSelection.endColumnIndex);
    const maxCol = Math.max(dragSelection.startColumnIndex, dragSelection.endColumnIndex);
    return columnIndex >= minCol && columnIndex <= maxCol;
  };

  // Check if a custom task cell is in selection
  const isCustomTaskCellInSelection = (taskId: string, columnIndex: number): boolean => {
    if (!customTaskDragSelection || customTaskDragSelection.controlId !== taskId) return false;
    const minCol = Math.min(customTaskDragSelection.startColumnIndex, customTaskDragSelection.endColumnIndex);
    const maxCol = Math.max(customTaskDragSelection.startColumnIndex, customTaskDragSelection.endColumnIndex);
    return columnIndex >= minCol && columnIndex <= maxCol;
  };

  // Render custom task timeline bars
  const renderCustomTaskTimelineBars = (task: CustomPlanTask, columnIndex: number) => {
    // Get pre-calculated row assignments for this task
    const rowAssignment = customTaskRowAssignments.get(task.id);
    const rowMap = rowAssignment?.rowMap || new Map();
    
    const sortedTimeslots = [...task.timeslots].sort((a, b) =>
      new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );

    const barsToRender = sortedTimeslots.map((timeslot, sortedIndex) => {
      const position = calculateTimelineBarPosition(
        timeslot,
        timelineView,
        timelineColumns.length,
        fiscalYear,
        fiscalYearEndDate
      );

      if (!position || position.startColumnIndex !== columnIndex) {
        return null;
      }

      // Get row index from pre-calculated overlap-aware assignment
      const rowIndex = rowMap.get(timeslot.id) ?? sortedIndex % MIN_BARS_FIT;
      
      const barColor = getBarColor(sortedIndex);
      const avatarColor = getAvatarColor(timeslot.userId);
      
      // Check if this timeslot is being edited
      const isEditPopoverOpen = customTaskPopoverOpen && 
        customTaskPopoverMode === 'edit' && 
        editingCustomTimeslot?.id === timeslot.id;

      const timelineBarContent = (
        <Tooltip
          key={`tooltip-${task.id}-${timeslot.id}-${sortedIndex}`}
          title={customTaskPopoverOpen ? null : (
            <UserTooltipContent>
              <div className="user-name">{timeslot.userName}</div>
              <div className="user-dates">
                {new Date(timeslot.startDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short' })} - {new Date(timeslot.endDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short' })}
              </div>
              <div style={{ marginTop: '8px', fontSize: '11px', color: '#9CA3AF' }}>
                Click to edit or remove
              </div>
            </UserTooltipContent>
          )}
          placement="top"
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '8px',
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
        <ClickableTimelineBar
          $leftPx={position.leftOffset}
          $widthPx={position.totalWidth}
          $color={barColor}
          $isStart={position.isStart}
          $isEnd={position.isEnd}
          $rowIndex={rowIndex}
          onClick={(e) => handleCustomTimeslotClick(e, task.id, timeslot)}
        >
          <UserAvatar $bgColor={avatarColor}>{getUserInitials(timeslot.userName)}</UserAvatar>
          <AuditorName>{timeslot.userName}</AuditorName>
        </ClickableTimelineBar>
        </Tooltip>
      );

      // If this timeslot is being edited, wrap with Popover
      if (isEditPopoverOpen) {
        return (
          <Popover
            key={`popover-${task.id}-${timeslot.id}`}
            content={renderCustomTaskPopoverContent()}
            title={null}
            trigger="click"
            open={true}
            onOpenChange={(open) => !open && handleCloseCustomTaskPopover()}
            placement="top"
            overlayInnerStyle={{ 
              boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
              border: 'none',
              minWidth: 300,
            }}
          >
            {timelineBarContent}
          </Popover>
        );
      }

      return timelineBarContent;
    }).filter(Boolean);
    
    return barsToRender.length > 0 ? barsToRender : null;
  };

  // Render custom task popover content
  const renderCustomTaskPopoverContent = () => {
    const isEditMode = customTaskPopoverMode === 'edit';
    const dateRange = customTaskDragSelection 
      ? formatDateRange(customTaskDragSelection.startColumnIndex, customTaskDragSelection.endColumnIndex)
      : editingCustomTimeslot
        ? `${new Date(editingCustomTimeslot.startDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })} - ${new Date(editingCustomTimeslot.endDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}`
        : '';

    return (
      <PopoverContent>
        <PopoverHeader>
          <span>{isEditMode ? 'Edit Assignment' : 'Assign Auditor'}</span>
          <PopoverCloseButton onClick={handleCloseCustomTaskPopover}>
            <CloseOutlined style={{ fontSize: 14 }} />
          </PopoverCloseButton>
        </PopoverHeader>
        
        <PopoverSection>
          <PopoverLabel>Date Range</PopoverLabel>
          <DateRangeDisplay>{dateRange}</DateRangeDisplay>
        </PopoverSection>
        
        <PopoverSection>
          <PopoverLabel>Select Auditor</PopoverLabel>
          <Select
            style={{ width: '100%' }}
            placeholder="Select an auditor"
            value={customTaskSelectedAuditorId}
            onChange={setCustomTaskSelectedAuditorId}
            suffixIcon={<UserOutlined />}
            labelRender={(props) => {
              const auditor = auditors.find(a => a.id === props.value);
              if (!auditor) return props.label;
              return (
                <SelectAuditorCell>
                  <SelectAvatar>{getUserInitials(auditor.name)}</SelectAvatar>
                  <SelectName>{auditor.name}</SelectName>
                </SelectAuditorCell>
              );
            }}
            options={auditors.map(a => ({
              value: a.id,
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 0' }}>
                  <SelectAvatar>{getUserInitials(a.name)}</SelectAvatar>
                  <div>
                    <div style={{ fontWeight: 500 }}>{a.name}</div>
                  </div>
                </div>
              ),
            }))}
          />
        </PopoverSection>
        
        <PopoverActions>
          {isEditMode && (
            <DeleteButton
              icon={<DeleteOutlined />}
              onClick={handleDeleteCustomTimeslot}
              loading={deletingCustomTimeslot}
            >
              Remove
            </DeleteButton>
          )}
          <Button 
            type="primary" 
            style={{ height: '36px' }}
            onClick={handleSaveCustomTimeslot}
            loading={savingCustomTimeslot}
            disabled={!customTaskSelectedAuditorId || (isEditMode && !!editingCustomTimeslot && customTaskSelectedAuditorId === editingCustomTimeslot.userId)}
          >
            {isEditMode ? 'Update' : 'Assign'}
          </Button>
        </PopoverActions>
      </PopoverContent>
    );
  };

  // Render the assign auditor popover content
  const renderPopoverContent = () => {
    const isEditMode = popoverMode === 'edit';
    const dateRange = dragSelection 
      ? formatDateRange(dragSelection.startColumnIndex, dragSelection.endColumnIndex)
      : editingAssignment
        ? `${new Date(editingAssignment.startDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })} - ${new Date(editingAssignment.endDate).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}`
        : '';

    return (
      <PopoverContent>
        <PopoverHeader>
          <span>{isEditMode ? 'Edit Assignment' : 'Assign Auditor'}</span>
          <PopoverCloseButton onClick={handleClosePopover}>
            <CloseOutlined style={{ fontSize: 14 }} />
          </PopoverCloseButton>
        </PopoverHeader>
        
        <PopoverSection>
          <PopoverLabel>Date Range</PopoverLabel>
          <DateRangeDisplay>{dateRange}</DateRangeDisplay>
        </PopoverSection>
        
        <PopoverSection>
          <PopoverLabel>Select Auditor</PopoverLabel>
          <Select
            style={{ width: '100%' }}
            placeholder="Select an auditor"
            value={selectedAuditorId}
            onChange={setSelectedAuditorId}
            suffixIcon={<UserOutlined />}
            labelRender={(props) => {
              const auditor = auditors.find(a => a.id === props.value);
              if (!auditor) return props.label;
              return (
                <SelectAuditorCell>
                  <SelectAvatar>{getUserInitials(auditor.name)}</SelectAvatar>
                  <SelectName>{auditor.name}</SelectName>
                </SelectAuditorCell>
              );
            }}
            options={auditors.map(a => ({
              value: a.id,
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '4px 0' }}>
                  <SelectAvatar>{getUserInitials(a.name)}</SelectAvatar>
                  <div>
                    <div style={{ fontWeight: 500 }}>{a.name}</div>
                  </div>
                </div>
              ),
            }))}
          />
        </PopoverSection>
        
        <PopoverActions>
          {isEditMode && (
            <DeleteButton
              icon={<DeleteOutlined />}
              onClick={handleDeleteAssignment}
              loading={savingAssignment}
            >
              Remove
            </DeleteButton>
          )}
          <Button 
            type="primary" 
            style={{ height: '36px' }}
            onClick={handleSaveAssignment}
            loading={savingAssignment}
            disabled={!selectedAuditorId || (isEditMode && !!editingAssignment && selectedAuditorId === editingAssignment.userId)}
          >
            {isEditMode ? 'Update' : 'Assign'}
          </Button>
        </PopoverActions>
      </PopoverContent>
    );
  };

  // Loading state
  if (loading) {
    return (
      <LoadingWrapper>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
      </LoadingWrapper>
    );
  }

  // Empty state
  // if (!loading && cycles.length === 0) {
  //   return (
  //     <Container>
  //       <HeaderRow>
  //         <TimelineSelect
  //           value={timelineView}
  //           onChange={(value) => setTimelineView(value as TimelineView)}
  //           options={viewOptions}
  //         />
  //       </HeaderRow>
  //       <EmptyStateWrapper>
  //         <EmptyStateText>
  //           No controls found. Controls marked as "In Scope" in Control Activities will appear here.
  //         </EmptyStateText>
  //       </EmptyStateWrapper>
  //     </Container>
  //   );
  // }

  return (
    <Container>
      {/* Header Row */}
      <HeaderRow>
        <TimelineSelect
          value={timelineView}
          onChange={(value) => setTimelineView(value as TimelineView)}
          options={viewOptions}
        />
      </HeaderRow>

      {/* Custom Planning Tasks Section */}
      <CycleSection $bgColor={CYCLE_COLORS[0]}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginLeft: '16px', marginRight: '16px', marginBottom: '16px' }}>
          <CycleHeader onClick={() => setPlanningTasksCollapsed(!planningTasksCollapsed)} style={{ marginBottom: 0, marginLeft: 0 }}>
            <CollapseIcon>
              {planningTasksCollapsed ? <RightOutlined /> : <DownOutlined />}
            </CollapseIcon>
            <CycleTitle>Planning Tasks</CycleTitle>
          </CycleHeader>
          {!planningTasksCollapsed && customPlanningTasks.length > 0 && !isExternalUser && (
            <AddItemButton text="Add Task" onClick={handleAddCustomTask} />
          )}
        </div>

        {!planningTasksCollapsed && (
          <>
            {loadingCustomTasks ? (
              <LoadingWrapper style={{ background: 'white', borderRadius: '6px' }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
              </LoadingWrapper>
            ) : customPlanningTasks.length === 0 ? (
              <TaskEmptyState>
                <TaskEmptyText>No planning tasks created</TaskEmptyText>
                {!isExternalUser ? <AddItemButton text="Add Task" onClick={handleAddCustomTask} /> : ""}
              </TaskEmptyState>
            ) : (
              <TableContainer>
                {/* Left Sticky Table */}
                <StickyTableWrapper>
                  <StickyTable>
                    <TableHead>
                      <tr>
                        <StickyHeaderCell $width={470}>Task</StickyHeaderCell>
                        {!isExternalUser ? <StickyHeaderCell $width={70}>Action</StickyHeaderCell> : ""}
                      </tr>
                    </TableHead>
                    <TableBody>
                      {customPlanningTasks.map((task) => {
                        const taskRowHeight = customTaskRowAssignments.get(task.id)?.rowHeight || BASE_ROW_HEIGHT;
                        return (
                        <StickyRow key={task.id} $rowHeight={taskRowHeight}>
                          <TaskNameCell $width={470} $rowHeight={taskRowHeight}>
                            {task.name}
                          </TaskNameCell>
                          {!isExternalUser ? <TaskActionsCell $width={70}>
                            <Dropdown
                              menu={{
                                items: [
                                  {
                                    key: 'edit',
                                    label: (
                                      <MenuItemContent>
                                        <EditOutlined />
                                        <MenuItemText>Edit Task</MenuItemText>
                                      </MenuItemContent>
                                    ),
                                    onClick: () => handleEditCustomTask(task),
                                  },
                                  {
                                    key: 'delete',
                                    label: (
                                      <MenuItemContent>
                                        <DeleteOutlined style={{ color: '#DC2626' }} />
                                        <MenuItemTextDanger>Delete Task</MenuItemTextDanger>
                                      </MenuItemContent>
                                    ),
                                    onClick: () => openDeleteTaskModal(task.id),
                                  },
                                ],
                              }}
                              trigger={['click']}
                              placement="bottomRight"
                            >
                              <ActionButton disabled={!isAdmin} onClick={(e) => e.stopPropagation()}>
                                <MoreOutlined />
                              </ActionButton>
                            </Dropdown>
                          </TaskActionsCell> : ""}
                        </StickyRow>
                      );
                      })}
                    </TableBody>
                  </StickyTable>
                </StickyTableWrapper>

                {/* Right Scrollable Table - Timeline */}
                <ScrollableTableWrapper>
                  <ScrollableTable>
                    <TableHead>
                      <tr>
                        {timelineColumns.map(col => (
                          <TimelineColumnHeader key={col.key} $viewMode={timelineView}>
                            {timelineView === 'week' && col.weekNum ? (
                              <Tooltip title={`Week ${col.weekNum}`} placement="top">
                                <span>{col.label}</span>
                              </Tooltip>
                            ) : (
                              col.label
                            )}
                          </TimelineColumnHeader>
                        ))}
                      </tr>
                    </TableHead>
                    <TableBody>
                      {customPlanningTasks.map((task) => {
                        const taskRowHeight = customTaskRowAssignments.get(task.id)?.rowHeight || BASE_ROW_HEIGHT;
                        return (
                        <ScrollableRow key={task.id} $rowHeight={taskRowHeight}>
                          {timelineColumns.map((col, colIndex) => {
                            const isInSelection = isCustomTaskCellInSelection(task.id, colIndex);
                            const isPopoverAnchor = customTaskPopoverOpen && 
                              customTaskPopoverPosition?.taskId === task.id && 
                              customTaskDragSelection?.startColumnIndex === colIndex;
                            
                            const cellContent = (
                              <TimelineCell 
                                key={col.key} 
                                $viewMode={timelineView}
                                $rowHeight={taskRowHeight}
                              >
                                {/* Selection highlight */}
                                <SelectionHighlight 
                                  $isSelected={false} 
                                  $isInRange={isInSelection && isCustomTaskDragging}
                                />
                                
                                {/* Clickable area for drag selection */}
                                <TimelineCellContent
                                  onMouseDown={(e) => handleCustomTaskCellMouseDown(task.id, colIndex, e)}
                                  onMouseEnter={() => handleCustomTaskCellMouseEnter(task.id, colIndex)}
                                  onMouseUp={() => handleCustomTaskCellMouseUp(task.id, colIndex)}
                                />
                                
                                {/* Timeline bars */}
                                {renderCustomTaskTimelineBars(task, colIndex)}
                              </TimelineCell>
                            );
                            
                            // Wrap with Popover only for the anchor cell
                            if (isPopoverAnchor) {
                              return (
                                <Popover
                                  key={col.key}
                                  content={renderCustomTaskPopoverContent()}
                                  title={null}
                                  trigger="click"
                                  open={customTaskPopoverOpen}
                                  onOpenChange={(open) => !open && handleCloseCustomTaskPopover()}
                                  placement="top"
                                  overlayInnerStyle={{ 
                                    boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
                                    border: 'none',
                                    minWidth: 300,
                                  }}
                                >
                                  {cellContent}
                                </Popover>
                              );
                            }
                            
                            return cellContent;
                          })}
                        </ScrollableRow>
                      );
                      })}
                    </TableBody>
                  </ScrollableTable>
                </ScrollableTableWrapper>
              </TableContainer>
            )}
          </>
        )}
      </CycleSection>

      {/* Side Panel/Drawer for Custom Task */}
      <RightContentDrawer
        open={customTaskDrawerOpen}
        title={editingCustomTask ? 'Edit Planning Task' : 'Add Planning Task'}
        onClose={() => setCustomTaskDrawerOpen(false)}
        width={600}
        footer={
          <ButtonGroup>
            <Button type="default" onClick={() => setCustomTaskDrawerOpen(false)}>
              Go back
            </Button>
            <Button 
              type="primary" 
              onClick={handleSaveCustomTask}
              disabled={!customTaskName.trim() || !isAdmin}
              loading={submitCustomTask}
            >
              {editingCustomTask ? 'Save changes' : 'Add item'}
            </Button>
          </ButtonGroup>
        }
      >
        <FormLabel>Task Name</FormLabel>
        <FormInput
          value={customTaskName}
          onChange={(e) => setCustomTaskName(e.target.value)}
          placeholder="Enter task name..."
          autoFocus
        />
      </RightContentDrawer>

      {/* Cycle Sections */}
      {cycles.map((cycle, cycleIndex) => {
        const isCollapsed = collapsedCycles.has(cycle.id);
        const bgColor = CYCLE_COLORS[(cycleIndex + 1) % CYCLE_COLORS.length];

        return (
          <CycleSection key={cycle.id} $bgColor={bgColor}>
            <CycleHeader onClick={() => toggleCycleCollapse(cycle.id)}>
              <CollapseIcon>
                {isCollapsed ? <RightOutlined /> : <DownOutlined />}
              </CollapseIcon>
              <CycleTitle>{cycle.cycleCode} {cycle.cycleName}</CycleTitle>
            </CycleHeader>

            {!isCollapsed && (
              <TableContainer>
                {/* Left Sticky Table */}
                <StickyTableWrapper>
                  <StickyTable>
                  <TableHead>
                    <tr>
                        <StickyHeaderCell $width={STICKY_COLUMN_WIDTHS.controls}>Controls</StickyHeaderCell>
                        <StickyHeaderCell $width={STICKY_COLUMN_WIDTHS.risk}>Risk ranking</StickyHeaderCell>
                        <StickyHeaderCell $width={STICKY_COLUMN_WIDTHS.testingStatus}>Testing Status</StickyHeaderCell>
                        <StickyHeaderCell $width={STICKY_COLUMN_WIDTHS.description}>Control Description</StickyHeaderCell>
                      </tr>
                    </TableHead>
                    <TableBody>
                      {cycle.controls.map((control) => {
                        const controlRowHeight = controlRowAssignments.get(control.id)?.rowHeight || BASE_ROW_HEIGHT;
                        return (
                        <StickyRow 
                          key={control.id}
                          $isHovered={hoveredControlId === control.id}
                          $rowHeight={controlRowHeight}
                          onMouseEnter={() => setHoveredControlId(control.id)}
                          onMouseLeave={() => setHoveredControlId(null)}
                        >
                          <StickyBodyCell $width={STICKY_COLUMN_WIDTHS.controls} $rowHeight={controlRowHeight}>
                            <ControlIdChip onClick={(e) => handleNavigateToControl(control.id, e)}>{control.controlCode}</ControlIdChip>
                          </StickyBodyCell>
                          <StickyBodyCell $width={STICKY_COLUMN_WIDTHS.risk} $rowHeight={controlRowHeight}>
                            <RiskRankingChip $level={control.riskRanking}>
                              {control.riskRanking}
                            </RiskRankingChip>
                          </StickyBodyCell>
                          <StickyBodyCell $width={STICKY_COLUMN_WIDTHS.testingStatus} $rowHeight={controlRowHeight} style={{ textAlign: 'center' }}>
                            {control.testingStatus.toFixed(2)}%
                          </StickyBodyCell>
                          <StickyBodyCell $width={STICKY_COLUMN_WIDTHS.description} $rowHeight={controlRowHeight}>
                            <DescriptionText $maxHeight={controlRowHeight} title={control.controlDescription.what}>
                              <span className="what-label">What:</span>{' '}
                              <span className="what-text">
                                {control.controlDescription.what.length > 50 
                                  ? `${control.controlDescription.what.substring(0, 50)}...` 
                                  : control.controlDescription.what}
                              </span>
                              <div className="when-text">When... Who... Why... How... Evidence...</div>
                            </DescriptionText>
                          </StickyBodyCell>
                        </StickyRow>
                      );
                      })}
                    </TableBody>
                  </StickyTable>
                </StickyTableWrapper>

                {/* Right Scrollable Table */}
                <ScrollableTableWrapper>
                  <ScrollableTable>
                    <TableHead>
                      <tr>
                      {timelineColumns.map(col => (
                          <TimelineColumnHeader key={col.key} $viewMode={timelineView}>
                          {timelineView === 'week' && col.weekNum ? (
                            <Tooltip
                              title={`Week ${col.weekNum}`}
                              placement="top"
                            >
                              <span>{col.label}</span>
                            </Tooltip>
                          ) : (
                            col.label
                          )}
                        </TimelineColumnHeader>
                      ))}
                    </tr>
                  </TableHead>
                  <TableBody>
                    {cycle.controls.map((control) => {
                        const controlRowHeight = controlRowAssignments.get(control.id)?.rowHeight || BASE_ROW_HEIGHT;
                        return (
                        <ScrollableRow 
                          key={control.id}
                          $isHovered={hoveredControlId === control.id}
                          $rowHeight={controlRowHeight}
                          onMouseEnter={() => setHoveredControlId(control.id)}
                          onMouseLeave={() => setHoveredControlId(null)}
                        >
                          {timelineColumns.map((col, colIndex) => {
                            const isInSelection = isCellInSelection(control.id, colIndex);
                            const isPopoverAnchor = popoverOpen && 
                              popoverPosition?.controlId === control.id && 
                              dragSelection?.startColumnIndex === colIndex;
                            
                            const cellContent = (
                              <TimelineCell key={col.key} $viewMode={timelineView} $rowHeight={controlRowHeight}>
                                {/* Selection highlight */}
                                <SelectionHighlight 
                                  $isSelected={false} 
                                  $isInRange={isInSelection && isDragging}
                                />
                                
                                {/* Clickable area for drag selection */}
                                <TimelineCellContent
                                  onMouseDown={(e) => handleCellMouseDown(control.id, colIndex, e)}
                                  onMouseEnter={() => handleCellMouseEnter(control.id, colIndex)}
                                  onMouseUp={() => handleCellMouseUp(control.id, colIndex)}
                                />
                                
                                {/* Timeline bars */}
                            {renderTimelineBars(control, colIndex)}
                          </TimelineCell>
                            );
                            
                            // Wrap with Popover only for the anchor cell
                            if (isPopoverAnchor) {
                              return (
                                <Popover
                                  key={col.key}
                                  content={renderPopoverContent()}
                                  title={null}
                                  trigger="click"
                                  open={popoverOpen}
                                  onOpenChange={(open) => !open && handleClosePopover()}
                                  placement="top"
                                  overlayInnerStyle={{ 
                                    boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
                                    border: 'none',
                                    minWidth: 300,
                                  }}
                                >
                                  {cellContent}
                                </Popover>
                              );
                            }
                            
                            return cellContent;
                          })}
                        </ScrollableRow>
                      );
                    })}
                  </TableBody>
                  </ScrollableTable>
                </ScrollableTableWrapper>
              </TableContainer>
            )}
          </CycleSection>
        );
      })}

      {/* Delete Task Confirmation Modal */}
      <ConfirmModal
        open={deleteTaskModalOpen}
        onClose={() => {
          setDeleteTaskModalOpen(false);
          setTaskToDelete(null);
        }}
        onConfirm={handleDeleteCustomTask}
        icon={trashIcon}
        title="Delete Planning Task"
        description="Are you sure you want to delete this task? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        loading={deletingTask}
      />
    </Container>
  );
};

export default TestingTimelineTab;
